import Spinner from './loading-spinner';

// It doesn't appear that we can use the afterprint event
// inside of iFrames, so instead we store callbacks to
// remove and clean up previous iFrames when a new one is clicked
let removeIframeCallbacks = [];

// Avoid flash when showing loading spinner for PDF buttons
const minSpinnerTime = 500;
const maxSpinnerTime = 1600;
const pdfLoadedSpinnerHideDelay = 500;

function printPdf(pdfPath, spinner) {
  if (!pdfPath) return;

  removeIframeCallbacks.forEach(cb => cb());
  removeIframeCallbacks = [];

  // Fallback in case PDF cannot be printed in iFrame
  const openPdf = () => {
    setIsLoading(false);
    window.open(pdfPath);
  };

  const setIsLoading = (isLoading, immediate) => {
    const toggleSpinner = isVisible => {
      if (isVisible) {
        spinner.show();
        spinner.parent.classList.add('loading');
      } else {
        spinner.hide();
        spinner.parent.classList.remove('loading');
      }
    };

    if (isLoading) {
      toggleSpinner(true);
    } else {
      if (immediate) {
        toggleSpinner(false);
        return;
      }

      setTimeout(() => toggleSpinner(false), pdfLoadedSpinnerHideDelay);
    }
  };

  try {
    let pdfUrl = pdfPath;

    if (pdfPath.startsWith('http')) {
      const url = new URL(pdfPath);

      if (url.hostname !== window.location.hostname) {
        openPdf();
        return;
      }

      pdfUrl = url.href;
    }

    setIsLoading(true);

    const isLoadingPromise = new Promise(resolve => {
      setTimeout(() => resolve(), minSpinnerTime);
    });

    setTimeout(() => setIsLoading(false), maxSpinnerTime);

    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    document.body.append(iframe);

    const removeIframe = () => {
      iframe.removeEventListener('load', handleLoad);
      iframe.removeEventListener('error', handleError);
      iframe.remove();
    };

    const handleLoad = () => {
      setTimeout(() => {
        if (!iframe.contentDocument) {
          openPdf();
          removeIframe();
          return;
        }

        const print = async () => {
          iframe.focus();
          iframe.contentWindow.print();
          removeIframeCallbacks.push(removeIframe);

          await isLoadingPromise;
          setIsLoading(false);
        };

        // Safari stops JavaScript execution while printing
        if (document.body.classList.contains('safari')) {
          setIsLoading(false, true);
          setTimeout(print, 1);
          return;
        }

        print();
      }, 100);
    };

    const handleError = () => {
      console.warn('Failed to load PDF:', pdfPath);
      removeIframe();
      setIsLoading(false);
    };

    iframe.addEventListener('load', handleLoad, { once: true });
    iframe.addEventListener('error', handleError, { once: true });

    iframe.src = pdfUrl;
  } catch {
    console.warn('Invalid PDF path:', pdfPath);
    openPdf();
  }
}

function printHtml(el) {
  if (!el.id) {
    window.print();
    return;
  }

  const bodyPrintingClass = 'printing-section';
  const bodySectionTargetClass = `print-${el.id}`;
  const sectionPrintingClass = 'printing-active';

  window.addEventListener(
    'afterprint',
    () => {
      document.body.classList.remove(bodyPrintingClass, bodySectionTargetClass);
      el.classList.remove(sectionPrintingClass);
    },
    { once: true }
  );

  document.body.classList.add(bodyPrintingClass, bodySectionTargetClass);
  el.classList.add('printing-active');

  window.print();
}

document.querySelectorAll('.btn-print').forEach(btn => {
  const target = btn.dataset.target?.trim();

  const spinner = new Spinner({
    parent: btn,
    size: 15,
    strokeWidth: 6
  });

  btn.addEventListener('click', e => {
    e.preventDefault();

    if (btn.classList.contains('loading')) return;

    // If no target is passed, or target is 'page' we
    // default to printing the entire page
    if (!target || target === 'page') {
      window.print();
      return;
    }

    // If the target refers to an HTML element, we
    // attempt to print the contents of that element
    if (target.startsWith('#')) {
      const el = document.querySelector(target);

      if (el) {
        printHtml(el);
      } else {
        console.warn('Print section not found:', target);
        window.print();
      }

      return;
    }

    // If the target is a URL (usually a PDF),
    // we attempt to print it in an iFrame
    // or fallback to opening the page
    printPdf(target, spinner);
  });
});
